/*
 * The i18n layer, architectural from the first component (direction §7, build
 * rule 8).
 *
 * NO LITERALS IN COMPONENTS, from the start. It was set up while only English
 * shipped, because the retrofit is what goes wrong: plural rules, locale-aware
 * number and date formatting, and RTL all have to be designed in, and a codebase
 * that grew with literals in JSX has to be rewritten to accept them.
 *
 * TIER-1 LOCALES after English (screens-and-navigation build rule 8):
 *   en, zh, es, fr, ru, sr, ar
 *
 * Arabic is in that set specifically as the RTL exercise the rest lacked, which
 * is why `dir` is derived here and applied to the document rather than assumed
 * to be ltr.
 */

import i18next from "i18next";
import { initReactI18next } from "react-i18next";

import en from "../../public/locales/en.json";
import { catalogueFormatOf, setDateLocale } from "../lib/format";

/**
 * The tier-1 locales. All seven ship as catalogues under `/locales/`; English
 * is also bundled as the fallback.
 */
export const TIER_1_LOCALES = ["en", "zh", "es", "fr", "ru", "sr", "ar"] as const;

export type Locale = (typeof TIER_1_LOCALES)[number];

/**
 * RTL locales among the tier-1 set. Derived rather than hard-coded per screen so
 * a new RTL locale is one entry here and not an audit of every component.
 */
const RTL_LOCALES = new Set<string>(["ar", "he", "fa", "ur"]);

export function directionFor(locale: string): "ltr" | "rtl" {
  // `noUncheckedIndexedAccess` is on, so split()[0] is possibly-undefined even
  // though it never is in practice. The `?? locale` is the honest narrowing
  // rather than a non-null assertion, which would just silence the checker.
  const base = locale.split("-")[0] ?? locale;
  return RTL_LOCALES.has(base) ? "rtl" : "ltr";
}

/**
 * Applies the locale to the document element.
 *
 * `lang` matters for more than screen readers: it selects the correct font
 * fallbacks for CJK, drives hyphenation, and is what makes
 * `Intl.DateTimeFormat` agree with what the page claims to be.
 */
export function applyDocumentLocale(locale: string): void {
  const root = document.documentElement;
  root.setAttribute("lang", locale);
  root.setAttribute("dir", directionFor(locale));
}

/**
 * Points the date and time formatters at a language: its names and digits
 * through Intl, and its catalogue's `format.date` patterns for the order.
 * Called BEFORE i18next switches, so the re-render the switch causes already
 * formats in the new language.
 */
function applyFormatLocale(locale: string): void {
  const bundle = locale === FALLBACK_LOCALE ? en : i18next.getResourceBundle(locale, "translation");
  setDateLocale(locale, catalogueFormatOf(bundle));
}

/**
 * The bundled fallback, which is never fetched.
 *
 * English is compiled into the client so there is ALWAYS a catalogue, whatever
 * the network did: a dead connection at a venue must render the app in English
 * rather than in dotted keys. Every other language is a file under
 * `/locales/`, fetched on demand.
 */
export const FALLBACK_LOCALE = "en";

/**
 * Fetch one catalogue and register it, or answer false.
 *
 * FALSE IS A REAL ANSWER and the caller depends on it: the resolution below
 * asks "does `es-MX` exist?" by asking for it, which is what lets a locale be
 * matched against the catalogues that ACTUALLY SHIPPED without a list of them
 * anywhere in this bundle (decision 537). A 404 is the ordinary case, not an
 * error.
 *
 * Registered rather than re-fetched on a second switch: i18next keeps the
 * bundle, so flipping back and forth costs one request each way at most.
 */
async function loadCatalogue(code: string): Promise<boolean> {
  if (code === FALLBACK_LOCALE) return true;
  if (i18next.hasResourceBundle(code, "translation")) return true;

  try {
    const res = await fetch(`/locales/${encodeURIComponent(code)}.json`);
    if (!res.ok) return false;
    const bundle = (await res.json()) as Record<string, unknown>;
    i18next.addResourceBundle(code, "translation", bundle, true, true);
    return true;
  } catch {
    // Offline, or a body that is not JSON. English stays in place, which is
    // the same outcome as a language that does not exist.
    return false;
  }
}

/**
 * The candidates for "whatever this device is set to", most specific first.
 *
 * FULL TAG THEN BASE TAG, because `es-MX` and `es` are different catalogues if
 * somebody ships both and the same one if they do not — and asking for the
 * specific one first is the only order that can tell them apart.
 */
function systemCandidates(): string[] {
  const tag = typeof navigator === "undefined" ? "" : navigator.language;
  if (!tag) return [];
  const base = tag.split("-")[0] ?? tag;
  return base === tag ? [tag] : [tag, base];
}

/**
 * Resolve and activate a locale, answering the code that actually took.
 *
 * `null` MEANS "FOLLOW THE DEVICE" (decision 537), which is the stored form of
 * the "System default" checkbox and is resolved HERE rather than on the server
 * — which is exactly why one account's two devices can legitimately render in
 * different languages. A stored code is tried on its own and does NOT fall
 * through to the device: somebody who chose a language and lost its catalogue
 * gets English, not a third language they never asked for.
 */
export async function setLocale(stored: string | null | undefined): Promise<string> {
  const candidates = stored ? [stored] : systemCandidates();

  for (const code of candidates) {
    if (await loadCatalogue(code)) {
      applyFormatLocale(code);
      await i18next.changeLanguage(code);
      applyDocumentLocale(code);
      return code;
    }
  }

  applyFormatLocale(FALLBACK_LOCALE);
  await i18next.changeLanguage(FALLBACK_LOCALE);
  applyDocumentLocale(FALLBACK_LOCALE);
  return FALLBACK_LOCALE;
}

/**
 * Initialise i18next and activate a locale before first paint.
 *
 * THE DEVICE'S LANGUAGE IS RESOLVED HERE AND THE ACCOUNT'S IS NOT, and the
 * split is deliberate. Rendering before i18next is ready would flash raw keys,
 * so this is awaited — but the stored preference is not known until `/api/me`
 * answers, and blocking first paint on that would make a dead connection a
 * blank app, which main.tsx's own catch exists to prevent. So the app boots in
 * the device's language, which is the right answer for a signed-out visitor and
 * for most signed-in ones, and `setLocale` switches when the account's
 * preference arrives. The transient is one render in the system language for
 * somebody who chose something else, which is honest and brief.
 */
export async function initI18n(locale?: string): Promise<void> {
  // English first, always: i18next has to come up with a complete catalogue,
  // and the device's language — which may be a fetch away, or may not exist —
  // is resolved against that rather than instead of it.
  await i18next.use(initReactI18next).init({
    lng: FALLBACK_LOCALE,
    fallbackLng: "en",
    resources: {
      en: { translation: en },
    },
    interpolation: {
      // React escapes for us; double-escaping would render entities literally.
      escapeValue: false,
    },
    /*
     * Loud in development, LOUDER in tests, silent in production.
     *
     * This is what the comment here always claimed and what the configuration
     * did not do: `saveMissing: false` disables the missing-key callback
     * entirely, so a key that did not exist rendered as its own dotted name and
     * nothing said a word.
     *
     * It is not hypothetical. Renaming `settings.trackedFencers` left
     * ManageFencers' heading pointing at the deleted key, and the screen
     * rendered the literal text "settings.trackedFencers" as its <h1>. What
     * caught it was an unrelated test asserting different copy — that is luck,
     * not coverage, and the same slip in a screen with thinner tests would have
     * shipped (decision 325).
     *
     * THROWING IN TESTS rather than warning, because a warning in a test run is
     * a line nobody reads. Every component takes its strings from here, so any
     * test that renders the screen becomes the check.
     *
     * Never in production: a missing key must never crash a venue phone, and the
     * dotted key is at least a string on screen rather than a blank one.
     */
    saveMissing: import.meta.env.MODE !== "production",
    missingKeyHandler: (_lngs, _ns, key) => {
      const message = `missing i18n key: ${key}`;
      if (import.meta.env.MODE === "test") throw new Error(message);
      console.error(message);
    },
    returnNull: false,
    // i18next prints a sponsor banner on every init otherwise, which is one
    // line per test file in a test run.
    showSupportNotice: false,
  });

  // An EXPLICIT locale is taken verbatim and resolves nothing — which is what
  // the test harness wants: a suite whose language depended on the machine's
  // would assert different copy in CI than on a laptop. No argument means
  // "follow the device", the boot case.
  if (locale !== undefined) {
    applyFormatLocale(locale);
    await i18next.changeLanguage(locale);
    applyDocumentLocale(locale);
    return;
  }

  await setLocale(null);
}

export default i18next;
